import React, {useState, ChangeEvent, FocusEvent, useCallback} from 'react';
import {currencyEditToNumericString, formatCurrencyValue, sanitizeCurrencyEdit} from './currencyInputParsing';

// TypeScript interface for the component props
interface CurrencyInputProps {
    /** The numeric value to display and edit */
    value: number;
    /** The ISO 4217 3-letter currency code (e.g., USD, EUR, MXN) */
    currency?: string;
    /** Whether to use commas for thousands separators */
    useCommas?: boolean;
    /** Whether to use European formatting (periods for thousands, commas for decimals) */
    useEuropean?: boolean;
    /** Accept a leading minus sign (a field that can hold a negative amount, such as a statement balance) */
    allowNegative?: boolean;
    /** Callback function when the value changes */
    onChange?: (value: number) => void;
    /** Callback function when the input loses focus */
    onBlur?: (value: number) => void;
    /** Optional ID for the input element */
    id?: string;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
                                                         value,
                                                         currency = 'USD',
                                                         useCommas = true,
                                                         useEuropean = false,
                                                         allowNegative = false,
                                                         onChange,
                                                         onBlur,
                                                         id
                                                     }) => {
    // Only store value while editing; null means use formatted prop value
    const [editingValue, setEditingValue] = useState<string | null>(null);
    const isEditing = editingValue !== null;

    // Get currency symbol based on ISO 4217 currency code
    const getCurrencySymbol = (currencyCode: string): string => {
        const symbols: Record<string, string> = {
            // Major currencies
            'USD': '$',      // US Dollar
            'EUR': '€',      // Euro
            'GBP': '£',      // British Pound
            'JPY': '¥',      // Japanese Yen
            'AUD': 'A$',     // Australian Dollar
            'CAD': 'C$',     // Canadian Dollar
            'CHF': 'Fr',     // Swiss Franc
            'CNY': '¥',      // Chinese Yuan
            'HKD': 'HK$',    // Hong Kong Dollar
            'NZD': 'NZ$',    // New Zealand Dollar

            // Latin American currencies
            'MXN': '$',      // Mexican Peso (officially just $, but often MX$ for clarity)
            'ARS': '$',      // Argentine Peso
            'BRL': 'R$',     // Brazilian Real
            'CLP': '$',      // Chilean Peso
            'COP': '$',      // Colombian Peso

            // European currencies
            'DKK': 'kr',     // Danish Krone
            'NOK': 'kr',     // Norwegian Krone
            'SEK': 'kr',     // Swedish Krona
            'PLN': 'zł',     // Polish Złoty
            'CZK': 'Kč',     // Czech Koruna

            // Asian currencies
            'INR': '₹',      // Indian Rupee
            'KRW': '₩',      // South Korean Won
            'SGD': 'S$',     // Singapore Dollar
            'THB': '฿',      // Thai Baht
            'IDR': 'Rp',     // Indonesian Rupiah

            // Middle Eastern & African currencies
            'AED': 'د.إ',    // UAE Dirham
            'SAR': '﷼',      // Saudi Riyal
            'ZAR': 'R',      // South African Rand
            'EGP': 'E£',     // Egyptian Pound
            'TRY': '₺',      // Turkish Lira

            // Other major currencies
            'RUB': '₽',      // Russian Ruble
            'ILS': '₪',      // Israeli Shekel
            'PHP': '₱',      // Philippine Peso
            'MYR': 'RM',     // Malaysian Ringgit
            'VND': '₫',      // Vietnamese Dong
        };

        // If the currency code exists in our mapping, return the symbol
        if (symbols[currencyCode]) {
            return symbols[currencyCode];
        }

        // For currencies we don't have a symbol for, return the currency code
        return currencyCode;
    };

    // Get display symbol (with clarification for ambiguous symbols like $)
    const getDisplaySymbol = (currencyCode: string): string => {
        const symbol = getCurrencySymbol(currencyCode);

        // Add prefix for disambiguation if needed (for currencies that use $ but aren't USD)
        if (symbol === '$' && currencyCode !== 'USD') {
            // Convert currency code to country code (first two letters)
            const countryCode = currencyCode.substring(0, 2);
            return countryCode + '$';
        }

        return symbol;
    };

    // Format the numeric value according to the specified options (rounds through the payments WASM helper)
    const formatValue = useCallback(
        (number: number): string => formatCurrencyValue(number, {useCommas, useEuropean}),
        [useCommas, useEuropean]
    );

    // Parse an input string back to a plain numeric string
    const parseValue = (inputStr: string): string => currencyEditToNumericString(inputStr, useEuropean);

    // Compute display value: use editing value if editing, otherwise format the prop
    const displayValue = isEditing ? editingValue : formatValue(value);

    const handleFocus = () => {
        // Start editing with the current formatted value
        setEditingValue(formatValue(value));
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const processed = sanitizeCurrencyEdit(e.target.value, {useEuropean, allowNegative});

        setEditingValue(processed);

        // Convert to number and call onChange
        if (onChange) {
            const numberValue = parseFloat(parseValue(processed));
            if (!isNaN(numberValue)) {
                onChange(numberValue);
            }
        }
    };

    const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
        // Get the numeric value from editing state
        const numericValue = parseFloat(parseValue(editingValue ?? ''));

        // Clear editing state - will now show formatted prop value
        setEditingValue(null);

        // Call onBlur handler with the current numeric value if provided
        if (onBlur) {
            onBlur(isNaN(numericValue) ? 0 : numericValue);
        }
    };

    const displaySymbol = getDisplaySymbol(currency);

    // Inline styles
    const containerStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%'
    };

    const symbolStyle: React.CSSProperties = {
        position: 'absolute',
        left: '8px',
        top: '50%',
        transform: 'translateY(-50%)'
    };

    const inputStyle: React.CSSProperties = {
        width: '100%',
        paddingLeft: '32px',
        paddingRight: '16px',
        paddingTop: '8px',
        paddingBottom: '8px',
        textAlign: 'right',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '16px',
        lineHeight: '1.5'
    };

    return (
        <div style={containerStyle}>
            <div style={symbolStyle}>
                {displaySymbol}
            </div>
            <input
                id={id}
                type="text"
                value={displayValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                style={inputStyle}
                placeholder="0.00"
                aria-label={`Amount in ${currency}`}
            />
        </div>
    );
};

export default CurrencyInput;